import { CONFIG } from '../config.js';
import { AmbientAudio } from './AmbientAudio.js';
import { FootstepAudio } from './FootstepAudio.js';
import { UISounds } from './UISounds.js';
import { CrowdAudio } from './CrowdAudio.js';
import { ShipAudio } from './ShipAudio.js';
import { MatrixBackgroundMusic } from './MatrixBackgroundMusic.js';
import { RainAudio } from './RainAudio.js';
import { VehicleTrafficAudio } from './VehicleTrafficAudio.js';

/**
 * Central Web Audio hub — modular buses, lifecycle, movement-driven ambience.
 * Drop-in `.wav` / `.ogg` paths via CONFIG.audio.assets when ready.
 */
export class AudioManager {
  /** @param {{ config?: typeof CONFIG.audio }} [options] */
  constructor({ config = CONFIG.audio } = {}) {
    this.config = config;
    /** @type {AudioContext | null} */
    this._ctx = null;
    /** @type {GainNode | null} */
    this._master = null;
    /** @type {GainNode | null} */
    this._ambientBus = null;
    /** @type {GainNode | null} */
    this._sfxBus = null;
    /** @type {GainNode | null} */
    this._musicBus = null;

    this.started = false;
    this.ambient = null;
    this.footsteps = null;
    this.ui = null;
    this.crowd = null;
    this.ships = null;
    this.music = null;
    this.rain = null;
    this.vehicles = null;

    /** @type {Record<string, string | null>} */
    this.assetPaths = { ...config.assets };

    /** @type {Map<string, AudioBuffer>} */
    this._buffers = new Map();
  }

  /** Call from first user gesture (menu click, canvas lock). */
  async ensureStarted() {
    if (!this.config.enabled) return;
    if (this.started && this._ctx?.state === 'running') return;

    if (!this._ctx) {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) {
        console.warn('[AudioManager] Web Audio not supported.');
        return;
      }
      this._ctx = new Ctx();

      this._master = this._ctx.createGain();
      this._master.gain.value = this.config.masterVolume;
      this._master.connect(this._ctx.destination);

      this._ambientBus = this._ctx.createGain();
      this._ambientBus.gain.value = this.config.ambientVolume;
      this._ambientBus.connect(this._master);

      this._sfxBus = this._ctx.createGain();
      this._sfxBus.gain.value = this.config.sfxVolume;
      this._sfxBus.connect(this._master);

      this._musicBus = this._ctx.createGain();
      this._musicBus.gain.value = this.config.musicVolume ?? 0.48;
      this._musicBus.connect(this._master);

      this.ambient = new AmbientAudio(this._ctx, this._ambientBus, this.config);
      this.footsteps = new FootstepAudio(this._ctx, this._sfxBus, {
        config: this.config.footsteps,
        getBuffer: (key) => this._buffers.get(key) ?? null,
      });
      this.ui = new UISounds(this._ctx, this._sfxBus, this.config.uiSounds);
      this.crowd = new CrowdAudio(
        this._ctx,
        this._ambientBus,
        this.config.crowd,
        this.config.seed + 17,
      );
      this.ships = new ShipAudio(this._ctx, this._ambientBus, this.config.ships);
      this.music = new MatrixBackgroundMusic(this._ctx, this._musicBus, this.config);
      this.rain = new RainAudio(
        this._ctx,
        this._ambientBus,
        this.config.rain,
        this.config.seed + 33,
      );
      this.vehicles = new VehicleTrafficAudio(
        this._ctx,
        this._ambientBus,
        this.config.vehicles,
      );

      await this._preloadOptionalAssets();
      this.ambient.start();
      this.crowd.start();
      this.ships.start();
      this.rain.start();
      this.vehicles.start();
      this.music.start(() => this._buffers.get('backgroundMusic') ?? null);
    }

    if (this._ctx.state === 'suspended') {
      await this._ctx.resume();
    }
    this.started = true;
  }

  /**
   * @param {number} delta
   * @param {{
   *   horizontalSpeed?: number;
   *   sprinting?: boolean;
   *   onGround?: boolean;
   *   inSimulation?: boolean;
   *   nearbyNpcCount?: number;
   *   shipAudio?: { distance: number; pan: number; audibleCount: number };
   *   menuOpen?: boolean;
   *   rainActive?: boolean;
   *   rainIntensity?: number;
   *   vehicleAudio?: {
   *     distance: number;
   *     pan: number;
   *     audibleCount: number;
   *     nearestSpeed: number;
   *   };
   * }} state
   */
  update(delta, state = {}) {
    if (!this.started || !this.ambient) return;
    this.ambient.update(delta, state);
    this.crowd?.update(delta, state);
    this.ships?.update(delta, state);
    this.vehicles?.update(delta, state);
    this.rain?.update(delta, state);
    this.music?.update(delta, state);
  }

  setMasterVolume(value) {
    if (this._master) this._master.gain.value = value;
  }

  dispose() {
    this.ambient?.dispose();
    this.footsteps?.dispose();
    this.ui?.dispose();
    this.crowd?.dispose();
    this.ships?.dispose();
    this.music?.dispose();
    this.rain?.dispose();
    this.vehicles?.dispose();
    this._buffers.clear();
    if (this._ctx) {
      this._ctx.close().catch(() => {});
    }
    this._ctx = null;
    this._master = null;
    this._ambientBus = null;
    this._sfxBus = null;
    this._musicBus = null;
    this.started = false;
  }

  async _preloadOptionalAssets() {
    if (!this._ctx) return;
    const entries = Object.entries(this.assetPaths);
    await Promise.all(
      entries.map(async ([key, url]) => {
        if (!url) return;
        try {
          const response = await fetch(url);
          const data = await response.arrayBuffer();
          const buffer = await this._ctx.decodeAudioData(data);
          this._buffers.set(key, buffer);
        } catch (err) {
          console.warn('[AudioManager] Failed to load asset', key, err);
        }
      }),
    );
  }
}
